import { Card, Grid, pn, Stack, Typography, useElementBreakpoints, ValidationErrors } from "@compass/core";
import { DatePicker, Input, Select, TimePicker } from "components";
import { StudentReportEventTrackingDisplay, StudentReportEventTrackingDisplayResource } from "generated/openApi";
import produce from "immer";
import ApiService from "services/apiService";

export const EventTrackingDisplay = (props: {
  display: StudentReportEventTrackingDisplay;
  setDisplay: (filter: StudentReportEventTrackingDisplay) => void;
  resource: StudentReportEventTrackingDisplayResource;
  validationErrors: ValidationErrors | string[];
}) => {
  const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();
  const properties = props.resource.properties || [];

  const serviceSubTypes = props.resource.serviceSubTypes?.filter(
    (x) => x.serviceTypeId === props.display.serviceTypeId
  );

  const eventDateField = () => {
    const propertyName = pn<StudentReportEventTrackingDisplay>("eventDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.display.eventDate}
        onChange={(value) =>
          props.setDisplay(
            produce(props.display, (draft) => {
              draft.eventDate = value;
            })
          )
        }
      />
    );
  };

  const eventNameField = () => {
    const propertyName = pn<StudentReportEventTrackingDisplay>("eventName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.display.eventName}
        onChange={(e) =>
          props.setDisplay(
            produce(props.display, (draft) => {
              draft.eventName = e.target.value;
            })
          )
        }
      />
    );
  };

  const providerNameField = () => {
    const propertyName = pn<StudentReportEventTrackingDisplay>("providerName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        freeSolo
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        inputValue={props.display.providerName}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, value) =>
          props.setDisplay(
            produce(props.display, (draft) => {
              draft.providerName = `${value.lastName}, ${value.firstName}`;
            })
          )
        }
        onInputChange={(e) =>
          props.setDisplay(
            produce(props.display, (draft) => {
              draft.providerName = e?.target?.value;
            })
          )
        }
        fetchFunction={(searchString: string) => ApiService.search.providers(searchString)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const serviceTypeField = () => {
    const propertyName = pn<StudentReportEventTrackingDisplay>("serviceTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.resource.serviceTypes?.find((x) => x.id === props.display.serviceTypeId)}
        options={props.resource.serviceTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setDisplay(
            produce(props.display, (draft) => {
              draft.serviceTypeId = value?.id;
              draft.serviceSubTypeId = undefined;
            })
          )
        }
      />
    );
  };

  const serviceSubTypeField = () => {
    const propertyName = pn<StudentReportEventTrackingDisplay>("serviceSubTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={serviceSubTypes?.find((x) => x.id === props.display.serviceSubTypeId)}
        options={serviceSubTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setDisplay(
            produce(props.display, (draft) => {
              draft.serviceSubTypeId = value?.id;
            })
          )
        }
      />
    );
  };

  const startTimeField = () => {
    const propertyName = pn<StudentReportEventTrackingDisplay>("startTime");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <TimePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.display.startTime}
        onChange={(value) =>
          props.setDisplay(
            produce(props.display, (draft) => {
              draft.startTime = value;
            })
          )
        }
      />
    );
  };

  const endTimeField = () => {
    const propertyName = pn<StudentReportEventTrackingDisplay>("endTime");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <TimePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.display.endTime}
        onChange={(value) =>
          props.setDisplay(
            produce(props.display, (draft) => {
              draft.endTime = value;
            })
          )
        }
      />
    );
  };

  return (
    <Card
      header={
        <Stack>
          <Typography>Display Items</Typography>
          <Typography variant="caption">
            These items will be displayed as header info when exported to PDF or excel.
          </Typography>
        </Stack>
      }
    >
      <Grid ref={breakpointsRef} container spacing="sm">
        <Grid item xs={smallFieldBreakpoints}>
          {eventDateField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {eventNameField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {providerNameField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {serviceTypeField()}
        </Grid>

        {serviceSubTypes && serviceSubTypes.length > 0 && (
          <Grid item xs={smallFieldBreakpoints}>
            {serviceSubTypeField()}
          </Grid>
        )}

        <Grid item xs={smallFieldBreakpoints}>
          {startTimeField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {endTimeField()}
        </Grid>
      </Grid>
    </Card>
  );
};
